import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Star, Cake, Users, CheckCircle, TrendingUp, Ticket } from "lucide-react";
import { API_URL } from "../../config";

/**
 * /admin/backstage — real-time production panel.
 * Admin-only (uses cookie from AuthContext). Refreshes on every SSE checkin event.
 */
export default function Backstage({ compact = false }) {
  const [stats, setStats] = useState(null);
  const [now, setNow] = useState(new Date());
  const [displayToken, setDisplayToken] = useState(null);
  const lastEventRef = useRef(null);
  const flashRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/backstage-stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) { console.error("Backstage stats fetch failed:", e); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Fetch display token once (needed for SSE since stream is now token-protected)
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/display-token`, { withCredentials: true })
      .then(r => setDisplayToken(r.data.token || ""))
      .catch(() => setDisplayToken(""));
  }, []);

  // Subscribe to SSE to refresh instantly on each check-in
  useEffect(() => {
    if (displayToken === null) return; // wait for token fetch
    const url = displayToken
      ? `${API_URL}/api/display/stream?token=${encodeURIComponent(displayToken)}`
      : `${API_URL}/api/display/stream`;
    const es = new EventSource(url);
    es.addEventListener("checkin", (e) => {
      try {
        const data = JSON.parse(e.data);
        lastEventRef.current = data;
        // Flash pulse
        if (flashRef.current) {
          flashRef.current.style.opacity = "1";
          setTimeout(() => { if (flashRef.current) flashRef.current.style.opacity = "0"; }, 1200);
        }
        load();
      } catch (err) { console.error("SSE checkin parse failed:", err); }
    });
    return () => es.close();
  }, [displayToken]);

  if (!stats) {
    return <div className={`${compact ? "py-8" : "min-h-screen"} bg-[#09090b] text-white flex items-center justify-center`}>A carregar…</div>;
  }

  const { totals, sessions, vips_arrived, birthdays_today, recent_checkins } = stats;
  const overallPercent = totals.total_paid > 0 ? Math.round(totals.total_checked_in * 100 / totals.total_paid) : 0;

  return (
    <div className={`${compact ? "" : "min-h-screen"} bg-[#09090b] text-white ${compact ? "" : "p-6"}`} data-testid="backstage-panel">
      {/* Green flash on new check-in */}
      <div ref={flashRef} style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(circle, rgba(34,197,94,0.18), transparent 70%)", opacity: 0, transition: "opacity 0.6s" }} />

      <div className={compact ? "" : "max-w-7xl mx-auto"}>
        {/* Header — hidden in compact (Scanner provides its own) */}
        {!compact && (
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-black tracking-tight">Backstage · Tempo Real</h1>
              <p className="text-zinc-500 text-sm">Atualizado automaticamente · {now.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> AO VIVO
            </div>
          </div>
        )}

        {/* Totals — hidden in compact (Scanner shows clickable unified stats) */}
        {!compact && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat icon={<Ticket className="w-5 h-5" />} label="Bilhetes Vendidos" value={totals.total_paid} accent="text-zinc-300" />
            <Stat icon={<CheckCircle className="w-5 h-5" />} label="Check-ins" value={`${totals.total_checked_in} / ${totals.total_paid}`} sub={`${overallPercent}%`} accent="text-emerald-400" />
            <Stat icon={<Star className="w-5 h-5" />} label="VIPs Chegaram" value={totals.total_vips_arrived} accent="text-amber-400" />
            <Stat icon={<Cake className="w-5 h-5" />} label="Aniversariantes Hoje" value={totals.total_birthdays_today} accent="text-pink-400" />
          </div>
        )}

        {/* Per-session occupancy bars */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-white/10 mb-6">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /> Ocupação por Sessão</h2>
          <p className="text-zinc-500 text-sm mb-5">Check-ins efectivos · lotação total</p>
          <div className="space-y-4">
            {sessions.length === 0 && <div className="text-zinc-500 text-center py-4">Ainda não há sessões criadas.</div>}
            {sessions.map(s => {
              const percent = s.total_seats > 0 ? Math.min(100, s.checked_in * 100 / s.total_seats) : 0;
              const salesPercent = s.total_seats > 0 ? Math.min(100, s.paid * 100 / s.total_seats) : 0;
              return (
                <div key={s.id} data-testid={`bs-session-${s.id}`}>
                  <div className="flex items-end justify-between mb-2">
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {s.room_name && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">
                            {s.room_name}
                          </span>
                        )}
                        <span className="font-semibold text-white">{s.name || "(sem nome)"}</span>
                      </div>
                      {s.subtitle && (
                        <div className="text-sm text-zinc-300 mt-1" data-testid="bs-session-subtitle">
                          {s.subtitle}
                        </div>
                      )}
                      <div className="text-[11px] text-zinc-500 mt-1">{s.date}{s.time ? ` · ${s.time}` : ""}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        <span className="text-emerald-400 font-medium">{s.checked_in} check-ins</span>
                        <span className="mx-1">/</span>
                        <span>{s.paid} vendidos</span>
                        <span className="mx-1">·</span>
                        <span>{s.total_seats} lugares</span>
                        {s.vip_checked_in > 0 && <span className="ml-2 text-amber-400">⭐ {s.vip_checked_in} VIP</span>}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-emerald-400 font-bold text-lg">{Math.round(percent)}%</div>
                    </div>
                  </div>
                  <div className="relative h-3 bg-zinc-800 rounded-full overflow-hidden">
                    {/* Sold bar (background) */}
                    <div className="absolute inset-y-0 left-0 bg-blue-500/30 rounded-full" style={{ width: `${salesPercent}%` }} />
                    {/* Checked-in bar (on top) */}
                    <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full transition-all" style={{ width: `${percent}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="pt-2 flex items-center gap-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> Check-in feito</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500/30" /> Vendido (por chegar)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-zinc-800" /> Disponível</span>
            </div>
          </div>
        </div>

        {/* Two columns: VIPs/Birthdays + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent check-ins */}
          <div className="bg-zinc-900 rounded-2xl p-6 border border-white/10 lg:col-span-2" data-testid="bs-recent">
            <h2 className="text-lg font-semibold mb-1">Check-ins Recentes</h2>
            <p className="text-zinc-500 text-sm mb-4">Últimos 20 · ordenados do mais recente</p>
            {recent_checkins.length === 0 ? (
              <div className="text-zinc-500 text-center py-8">Ainda ninguém fez check-in.</div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                {recent_checkins.map(r => (
                  <div key={r.ticket_number} className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg hover:bg-zinc-900 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {r.first_name} {r.last_name}
                          {r.is_vip && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300">⭐ VIP</span>}
                          {r.group_code && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-mono">{r.group_code}</span>}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">{r.ticket_number}</div>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500 shrink-0 ml-2">
                      {formatTime(r.checked_in_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* VIPs + Birthdays */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/30 rounded-2xl p-6" data-testid="bs-vips">
              <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Star className="w-5 h-5 text-amber-400" /> VIPs Chegaram</h2>
              {vips_arrived.length === 0 ? (
                <div className="text-zinc-500 text-sm py-3">Nenhum VIP fez check-in ainda.</div>
              ) : (
                <div className="space-y-2 mt-3">
                  {vips_arrived.map(v => (
                    <div key={v.ticket_number} className="flex items-center justify-between p-2 bg-amber-500/5 rounded">
                      <span className="text-sm">{v.first_name} {v.last_name}</span>
                      <span className="text-xs text-zinc-500">{formatTime(v.checked_in_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gradient-to-br from-pink-500/10 to-pink-500/5 border border-pink-500/30 rounded-2xl p-6" data-testid="bs-birthdays">
              <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Cake className="w-5 h-5 text-pink-400" /> Aniversariantes Hoje</h2>
              {birthdays_today.length === 0 ? (
                <div className="text-zinc-500 text-sm py-3">Nenhum aniversariante fez check-in hoje.</div>
              ) : (
                <div className="space-y-2 mt-3">
                  {birthdays_today.map(b => (
                    <div key={b.ticket_number} className="flex items-center justify-between p-2 bg-pink-500/5 rounded">
                      <span className="text-sm">🎂 {b.first_name} {b.last_name}</span>
                      <span className="text-xs text-zinc-500">{formatTime(b.checked_in_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, accent }) {
  return (
    <div className="bg-zinc-900 rounded-2xl p-5 border border-white/10">
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-2 ${accent || "text-zinc-400"}`}>
        {icon} {label}
      </div>
      <div className="text-3xl font-black">{value}</div>
      {sub && <div className={`text-sm mt-1 ${accent || "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  } catch (_) { return ""; }
}
